import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Grid,
  Paper,
  InputBase,
  IconButton,
  Box,
  Tab,
} from "@material-ui/core";
import { TabList, TabPanel, TabContext } from '@material-ui/lab';
import { Search as SearchIcon } from "@material-ui/icons";
import Widget from "../../components/Widget";
import MemTable from "./components/MemTable";
import PageTitle from "../../components/PageTitle";
import PlanMemTable from "./components/PlanMemTable";
import TrialMemTable from "./components/TrialMemTable";
import styled, { keyframes } from 'styled-components';
import { Btn } from '../../styled-components/basicStyle';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// 전역 상태 관리
import { useRecoilState } from "recoil";
import { memberTableState } from "../../recoil/tableSetting";

// 데이터 정비
function combineData(arr, index1, index2, index3, index4, index5, index6, index7) {
  arr.forEach((item) => {
    const value1 = item[index1];
    const value2 = item[index2];
    const value3 = item[index3];
    const value4 = item[index4];
    const value5 = item[index5];
    const value6 = item[index6];
    const value7 = item[index7];
    item[index1] = value1.split('T')[0];
    item[index2] = value2.split('T')[0];
    if (value3 == null) {
      item[index3] = 0;
    }
    if (value4 == null) {
      item[index4] = 0;
    }
    if (value6 == null) {
      item[index6] = 0;
    }
    if (value7 == null) {
      item[index6] = 0;
    }
    if (value5 == null) {
      item[index5] = "기본";
    }
  })
  return arr;
}

export default function Member() {
  // pagination 변수들: 받아오는 리스트, 나열할 리스트, 페이지
  const [tableList, setTableList] = useState([]);
  const [planList, setPlanList] = useState([]);
  const [trialList, setTrialList] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const row = ["NO", "이름", "포인트", "마일리지", "메일", "연락처", "소속", "프로젝트 수", "가입일"];
  const searchTargets = ["name", "email", "org_name", "org_type"];
  const [memberTable, setMemberTable] = useRecoilState(memberTableState); // Recoil 상태 사용

  // search
  const getValue = (e) => {
    setSearchInput(e.target.value);
  };

  const [excelLoading, setExcelLoading] = useState(null);

  const dash = (value) => (value == null || value === '' ? '-' : value);

  const toMemberRows = (members) => members.map((member) => ({
    '이름': dash(member.name),
    '메일': dash(member.email),
    '연락처': dash(member.phone_number),
    '소속유형': dash(member.org_type),
    '소속명': dash(member.org_name),
    '가입일': dash(member.join_date),
    '통합 프로젝트 개수': member.total_project_cnt ?? 0,
    '특허 프로젝트 개수': member.patents_project_cnt ?? 0,
    '유효 플랜 여부': member.has_active_plan === 'Y' ? 'Y' : 'N',
    '플랜명': dash(member.plan_name),
    '플랜 시작일': dash(member.start_date),
    '플랜 종료일': dash(member.end_date),
  }));

  const downloadMembersExcel = async (mode) => {
    if (excelLoading) return;
    setExcelLoading(mode);
    try {
      const response = await axios.get('/api/member/allMemberExcel', { timeout: 120000 });
      let members = Array.isArray(response.data.data) ? response.data.data : [];
      if (mode === 'active') {
        members = members.filter((member) => member.has_active_plan === 'Y');
      }
      members = [...members].sort((a, b) => {
        const aDate = a.join_date || '';
        const bDate = b.join_date || '';
        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return bDate.localeCompare(aDate);
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(toMemberRows(members));
      ws['!cols'] = [
        { wch: 12 },
        { wch: 28 },
        { wch: 16 },
        { wch: 12 },
        { wch: 28 },
        { wch: 14 },
        { wch: 16 },
        { wch: 16 },
        { wch: 14 },
        { wch: 28 },
        { wch: 14 },
        { wch: 14 },
      ];
      const isActive = mode === 'active';
      XLSX.utils.book_append_sheet(wb, ws, isActive ? '유효 회원' : '전체 회원');

      const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const today = new Date();
      const dateStr =
        today.getFullYear() +
        String(today.getMonth() + 1).padStart(2, '0') +
        String(today.getDate()).padStart(2, '0');
      saveAs(blob, `${isActive ? '플랜회원목록' : '전체회원정보'}_${dateStr}.xlsx`);
    } catch (error) {
      console.error('회원 엑셀 다운로드 실패:', error);
      alert('엑셀 다운로드 중 오류가 발생했습니다.');
    } finally {
      setExcelLoading(null);
    }
  };

  const tabChange = (event, table) => {
    setMemberTable(table);
  };

  const headerActions = (
    <HeaderActions>
      <DownloadAction>
        <ExcelBtn type="button" disabled={!!excelLoading} onClick={() => downloadMembersExcel('all')}>
          {excelLoading === 'all' ? (
            <>
              <Spinner />
              엑셀 생성 중
            </>
          ) : (
            <>
              전체 회원 정보 다운로드
              <CountBadge>excel</CountBadge>
            </>
          )}
        </ExcelBtn>
        <DownloadHint>
          모든 회원 정보를 엑셀 파일로 저장합니다.
        </DownloadHint>
      </DownloadAction>
      <DownloadAction>
        <ExcelBtn type="button" disabled={!!excelLoading} onClick={() => downloadMembersExcel('active')}>
          {excelLoading === 'active' ? (
            <>
              <Spinner />
              엑셀 생성 중
            </>
          ) : (
            <>
              플랜 회원 정보 다운로드
              <CountBadge>excel</CountBadge>
            </>
          )}
        </ExcelBtn>
        <DownloadHint>
          현재 유효한 플랜을 이용 중인<br />회원 정보를 엑셀 파일로 저장합니다.
        </DownloadHint>
      </DownloadAction>
      <Link to="/app/researchAll/member/joinNew">
        <Btn>Trial 계정 생성</Btn>
      </Link>
    </HeaderActions>
  );

  // 전체 회원 API
  useEffect(() => {
    axios.get("/api/member/list")
      .then((response) => {
        setTableList(combineData(
          [...response.data.data.member],
          "sub_data",
          "last_modified_date",
          "total_project_cnt",
          "patents_project_cnt",
          "grade_name",
          "point_amount",
          "mileage_amount").reverse());
      })
      .catch(function (error) {
        console.log(error);
      });
  }, []);

  // 플랜 회원 API
  useEffect(() => {
    axios.get("/api/member/planMemList")
      .then((response) => {
        setPlanList(Array.isArray(response.data.data) ? response.data.data : []);
      })
      .catch(function (error) {
        console.log(error);
      });
  }, []);

  // 트라이얼 회원 API
  useEffect(() => {
    axios.get("/api/member/trialMembersWithStatus")
      .then((response) => {
        setTrialList(Array.isArray(response.data.data) ? response.data.data : []);
      })
  }, []);

  return (
    <div>
      <PageTitle title="회원 정보" />
      <Box>
        <TabContext value={memberTable}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              "& .PrivateTabIndicator-root-62.PrivateTabIndicator-colorSecondary-64.MuiTabs-indicator":
                { backgroundColor: "#FF5C93" },
            }}
            style={{ paddingLeft: "24px" }}
          >
            <TabList onChange={tabChange}>
              <Tab label="전체 회원" value="total" />
              <Tab label="플랜 회원" value="plan" />
              <Tab label="Trial 회원" value="trial" />
            </TabList>
          </Box>

          <TabPanel value="total" label="전체 회원">
            <div style={{ display: "flex", gap: 2, alignItems: "flex-start", justifyContent: "space-between"}}>
              <SearchPaper
                component="form"
                sx={{ display: "flex", alignItems: "center", width: "400" }}
              >
                <IconButton
                  type="button"
                  sx={{ p: "10px" }}
                  aria-label="search"
                >
                  <SearchIcon />
                </IconButton>
                <SearchInput
                  placeholder="Search"
                  inputProps={{ "aria-label": "Search" }}
                  onChange={getValue}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                    }
                  }}
                />
              </SearchPaper>
              {headerActions}
            </div>
            <Grid item xs={12}>
              <Widget>
                <MemTable
                  row={row}
                  filterKeyword={searchInput}
                  totalData={tableList}
                  searchTargets={searchTargets}
                />
              </Widget>
            </Grid>
          </TabPanel>

          <TabPanel value="plan" label="플랜 회원">
            <div style={{
              display: "flex",
              gap: 2,
              alignItems: "flex-start",
              justifyContent: "space-between",
              marginBottom: "24px"
            }}>
              <SearchPaper
                component="form"
                sx={{ display: "flex", alignItems: "center", width: "400" }}
              >
                <IconButton
                  type="button"
                  sx={{ p: "10px" }}
                  aria-label="search"
                >
                  <SearchIcon />
                </IconButton>
                <SearchInput
                  placeholder="Search"
                  inputProps={{ "aria-label": "Search" }}
                  onChange={getValue}
                />
              </SearchPaper>
              {headerActions}
            </div>
            <Grid item xs={12}>
              <Widget>
                <PlanMemTable
                  filterKeyword={searchInput}
                  totalData={planList}
                  searchTargets={searchTargets}
                />
              </Widget>
            </Grid>
          </TabPanel>

          <TabPanel value="trial" label="Trial 회원">
            <div style={{
              display: "flex",
              gap: 2,
              alignItems: "flex-start",
              justifyContent: "space-between",
              marginBottom: "24px"
            }}>
              <SearchPaper
                component="form"
                sx={{ display: "flex", alignItems: "center", width: "400" }}
              >
                <IconButton
                  type="button"
                  sx={{ p: "10px" }}
                  aria-label="search"
                >
                  <SearchIcon />
                </IconButton>
                <SearchInput
                  placeholder="Search"
                  inputProps={{ "aria-label": "Search" }}
                  onChange={getValue}
                />
              </SearchPaper>
              {headerActions}
            </div>
            <Grid item xs={12}>
              <Widget>
                <TrialMemTable
                  filterKeyword={searchInput}
                  totalData={trialList}
                  searchTargets={searchTargets}
                />
              </Widget>
            </Grid>
          </TabPanel>

        </TabContext>
      </Box>
    </div>
  );
}

const SearchInput = styled(InputBase)`
    display: inline-flex;
    width: 80%;
`;

const SearchPaper = styled(Paper)`
    width: 20%;
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
`;

const DownloadAction = styled.div`
    display: inline-flex;
    flex-direction: column;
    align-items: stretch;
`;

const ExcelBtn = styled(Btn)`
    width: 260px;
    padding: 0;
    background-color: #38761D;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    box-shadow: 0 3px 8px rgba(56, 118, 29, 0.35);

    &:hover {
        background-color: #2e5c16;
        box-shadow: 0 5px 12px rgba(56, 118, 29, 0.42);
    }

    &:disabled {
        background-color: #9aa0a6;
        box-shadow: none;
        cursor: not-allowed;
        opacity: 1;
    }
`;

const CountBadge = styled.span`
    min-width: 28px;
    height: 22px;
    padding: 0 7px;
    border-radius: 11px;
    background: rgba(255, 255, 255, 0.2);
    font-size: 12px;
    font-weight: 600;
    line-height: 22px;
    text-align: center;
`;

const DownloadHint = styled.p`
    width: 0;
    min-width: 100%;
    margin: 8px 2px 0;
    font-size: 12px;
    line-height: 1.45;
    color: #8b8b8b;
    text-align: center;
    word-break: keep-all;
`;

const spin = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
`;

const Spinner = styled.span`
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.35);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: ${spin} 0.8s linear infinite;
    flex-shrink: 0;
`;
